/**
 * Platform differences that keyboard shortcuts have to respect.
 *
 * Two of them, and the second is the one that bites:
 *
 *  1. The primary modifier is ⌘ on Apple platforms and Ctrl everywhere else.
 *     A Mac user pressing ⌘+D expects mute; pressing literal Ctrl+D is not
 *     something anyone does.
 *
 *  2. **Option changes the character.** On macOS, ⌥+S yields `event.key === "ß"`,
 *     ⌥+C yields "ç", ⌥+U yields a dead key. Matching on `event.key` therefore
 *     fails for every Alt shortcut on a Mac — the letter the user pressed is not
 *     the letter the event reports. Matching on `event.code` (the physical key)
 *     is immune to this, and to keyboard layout in general.
 */

/** True on macOS, iPadOS and iOS. */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false

  // userAgentData is the non-deprecated source where it exists (Chromium).
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
  if (uaData?.platform) return /mac|ios|ipad|iphone/i.test(uaData.platform)

  const platform = navigator.platform || ''
  const ua = navigator.userAgent || ''
  // iPadOS 13+ reports "MacIntel" and only distinguishes itself by touch points.
  return /mac|iphone|ipad|ipod/i.test(platform) || /mac os x/i.test(ua)
}

/**
 * The `KeyboardEvent.code` for a shortcut key, or null when the key has no
 * stable physical code we can rely on (punctuation like `?`, whose position
 * moves between layouts).
 */
export function codeForKey(key: string): string | null {
  if (/^[a-z]$/i.test(key)) return `Key${key.toUpperCase()}`
  if (/^[0-9]$/.test(key)) return `Digit${key}`
  return null
}

/** Symbols Apple users expect; words everyone else does. */
export function formatShortcut(keys: string): string {
  if (!isApplePlatform()) return keys
  return keys
    .split(' + ')
    .map((part) => {
      switch (part) {
        case 'Ctrl':
          return '⌘'
        case 'Alt':
          return '⌥'
        case 'Shift':
          return '⇧'
        default:
          return part
      }
    })
    .join(' + ')
}
